
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Lock, Database, Layers, RefreshCcw } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTheme } from "@/components/theme/ThemeProvider";
import { useMobile } from '@/hooks/use-mobile';

const ProcessStep = ({ 
  icon: Icon, 
  title, 
  description, 
  color,
  textColor = "text-white",
  isLast = false
}: { 
  icon: React.ElementType; 
  title: string; 
  description: string; 
  color: string;
  textColor?: string;
  isLast?: boolean;
}) => {
  const isMobile = useMobile();
  
  return (
    <div className="flex flex-col items-center">
      <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full ${color} flex items-center justify-center ${textColor} mb-2 md:mb-4 shadow-lg`}>
        <Icon className="w-6 h-6 md:w-8 md:h-8" />
      </div>
      <h3 className="text-base md:text-lg font-semibold mb-1">{title}</h3>
      {!isMobile && (
        <p className="text-muted-foreground text-sm text-center max-w-[200px]">{description}</p>
      )}
      
      {!isLast && !isMobile && (
        <div className="hidden md:flex items-center justify-center my-4 text-muted-foreground">
          <ArrowRight className="w-6 h-6" />
        </div>
      )}
    </div>
  );
};

const SecurityProcess = () => {
  const { theme } = useTheme();
  const isMobile = useMobile();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  
  return (
    <Card>
      <CardContent className={`${isMobile ? 'pt-4 px-2' : 'pt-6'}`}>
        <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-center">Our 4S Security Process</h2>
        
        <div className={`flex flex-wrap ${isMobile ? 'justify-around gap-y-4' : 'justify-between'} items-start`}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <ProcessStep 
                    icon={Database}
                    title="Shrink"
                    description="Compress data to minimize storage footprint and reduce transfer times"
                    color="bg-blue-500"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Advanced compression algorithms reduce data size by up to 80%</p>
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <ProcessStep 
                    icon={Layers}
                    title="Shred"
                    description="Break data into fragments distributed across multiple secure locations"
                    color="bg-indigo-500"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Redundant data sharding ensures no single point of failure</p>
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <ProcessStep 
                    icon={Lock}
                    title="Secure"
                    description="Encrypt using Post-Quantum Cryptography to ensure long-term protection"
                    color={isDark ? "bg-purple-500 dark:bg-purple-600" : "bg-purple-500"}
                    textColor="text-white"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Quantum-resistant encryption protects against future quantum computing threats</p>
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <ProcessStep 
                    icon={RefreshCcw}
                    title="Restore"
                    description="Multi-factor authenticated reassembly of data fragments when access is required"
                    color="bg-green-500"
                    isLast={true}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Biometric and token-based authentication ensures only authorized access</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {!isMobile && (
          <div className="mt-8 text-sm text-muted-foreground text-center">
            <p>Our patented 4S process ensures your data remains secure even in the face of quantum computing advances.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SecurityProcess;
